import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

/** Pull the widget access-token from whatever shape MSG91 currently returns.
 *  Different widget versions use different keys — we accept them all so the
 *  same bridge works for any deployment (and even if MSG91 ships a new key). */
function pickMsg91Token(input) {
  if (!input) return null;
  if (typeof input === "string") return input.trim() || null;
  const d = input;
  const flat =
    d.message ||
    d.access_token ||
    d["access-token"] ||
    d.token ||
    (d.data && (d.data.message || d.data.access_token || d.data["access-token"] || d.data.token));
  if (typeof flat === "string" && flat.trim()) return flat.trim();
  return null;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined=loading, null=unauth, obj=auth
  const [token, setToken] = useState(() => localStorage.getItem("mpn_token") || null);

  const bootstrap = useCallback(async () => {
    const t = localStorage.getItem("mpn_token");
    if (!t) { setUser(null); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem("mpn_token");
      setToken(null);
      setUser(null);
    }
  }, []);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  const setSession = (t, u) => {
    if (t) localStorage.setItem("mpn_token", t);
    setToken(t);
    setUser(u);
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setSession(data.token, data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setSession(data.token, data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("mpn_token");
    setToken(null);
    setUser(null);
  };

  const refresh = bootstrap;

  /** Exchange a MSG91 widget access-token for an AcresDeal session.
   *  `options.mode` is "login" (default) or "register".
   *  `options.profile` is used only for register mode.
   *  Throws an Error with `.code` when the backend returns a structured
   *  error (NO_ACCOUNT for login on unknown mobile, MOBILE_EXISTS /
   *  EMAIL_EXISTS for register conflicts). */
  const loginWithMsg91 = useCallback(async (widgetData, options = {}) => {
    const t = pickMsg91Token(widgetData);
    if (!t) throw new Error("MSG91 did not return an access token");
    const { mode = "login", profile } = options;
    try {
      const { data } = await api.post("/auth/msg91", { token: t, mode, profile });
      setSession(data.token, data.user);
      return data; // { token, user, is_new_user, mobile }
    } catch (err) {
      // Extract backend structured error code so callers can react
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code) {
        const e = new Error(detail.message || "Authentication failed");
        e.code = detail.code;
        e.mobile = detail.mobile;
        e.status = err.response?.status;
        throw e;
      }
      // Backend returns a friendly string on generic MSG91 errors
      // (e.g. "OTP verification failed: invalid access-token"). Surface it
      // so the user sees the real reason instead of "Request failed…".
      if (typeof detail === "string" && detail) {
        const e = new Error(detail);
        e.status = err.response?.status;
        throw e;
      }
      throw err;
    }
  }, []);

  /** Global bridge: MSG91 widget `success:` callback can invoke either
   *  `window.acresdealLoginWithMsg91(data)` or
   *  `window.acresdealRegisterWithMsg91(data, profile)` — pages set the
   *  right one when opening the widget. The legacy
   *  `acresdealCompleteMsg91Login` name is retained for any external code
   *  that still calls it, defaulting to LOGIN mode. */
  useEffect(() => {
    const login = async (widgetData) => {
      const result = await loginWithMsg91(widgetData, { mode: "login" });
      toast.success("Logged in successfully");
      return result;
    };
    const register = async (widgetData, profile) => {
      const result = await loginWithMsg91(widgetData, { mode: "register", profile });
      toast.success("Welcome to AcresDeal! Account created.");
      return result;
    };
    window.acresdealLoginWithMsg91 = login;
    window.acresdealRegisterWithMsg91 = register;
    window.acresdealCompleteMsg91Login = login; // legacy alias
    return () => {
      try {
        delete window.acresdealLoginWithMsg91;
        delete window.acresdealRegisterWithMsg91;
        delete window.acresdealCompleteMsg91Login;
      } catch { /* noop */ }
    };
  }, [loginWithMsg91]);

  return (
    <AuthContext.Provider value={{ user, token, login, register, setSession, logout, refresh, formatApiError, loginWithMsg91 }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
